import { push } from '@lib/analytics';
import {
    addToCart as addToCartEvent,
    removeFromCart as removeFromCartEvent
} from '@lib/analytics/google';
import { type CartLineItem } from '@lib/entities/Cart';
import { type ApplicationState, useApplicationState } from '@lib/state';
import { useEffect, useState } from 'react';
import useToast from './useToast';

const selectCart = (s: ApplicationState) => s.cart;
export const useCart = () => {
    const { addToCart, removeFromCart, ...state } = useApplicationState(selectCart);
    const { toast } = useToast();
    const [giftcardData, setGiftcardData] = useState<
        { id: number; action: 'add' | 'remove' } | undefined
    >(undefined);

    useEffect(() => {
        const { quote } = state;

        if (giftcardData && giftcardData.action === 'add') {
            // Quote was reset to undefined (API error) — revert the optimistic
            // gift card addition so the UI no longer shows it as applied.
            if (!quote) {
                state.removeGiftcard(giftcardData.id);
                setGiftcardData(undefined);
                return;
            }

            const appliedMethod = quote.payment_methods?.find(
                (pm) => pm.type === 'gift_card' && pm.gift_card_ids?.includes(giftcardData.id)
            );

            if (appliedMethod) {
                if (appliedMethod.status === 'failed' || appliedMethod.status === 'skipped') {
                    toast({ level: 'error', message: 'Gift Card could not be applied to order' });
                    state.removeGiftcard(giftcardData.id);
                } else {
                    toast({ level: 'info', message: 'Gift Card applied to order' });
                }
                setGiftcardData(undefined);
            }
        }
        if (giftcardData && giftcardData.action === 'remove') {
            let hasGiftCard = false;
            quote?.payment_methods?.forEach((eachPaymentMethod) => {
                if (
                    eachPaymentMethod.type === 'gift_card' &&
                    !eachPaymentMethod.gift_card_ids?.includes(giftcardData.id)
                ) {
                    hasGiftCard = true;
                    toast({ level: 'info', message: 'Gift Card removed from order' });
                    setGiftcardData(undefined);
                }
            });

            if (!hasGiftCard) {
                toast({ level: 'info', message: 'Gift Card removed from order' });
                setGiftcardData(undefined);
            }
        }
    }, [state.quote]);

    const onAddToCart = (item: CartLineItem) => {
        addToCart(item);
        push(addToCartEvent(item));
    };

    const onRemoveFromCart = (item: CartLineItem) => {
        removeFromCart(item);
        push(removeFromCartEvent(item));
    };

    const applyGiftcardAndUpdateQuote = (giftcardId: number) => {
        const { applyGiftcard, updateQuote } = state;
        setGiftcardData({ id: giftcardId, action: 'add' });
        applyGiftcard(giftcardId);
        updateQuote();
    };
    const removeGiftcardAndUpdateQuote = (giftcardId: number) => {
        const { removeGiftcard, updateQuote } = state;
        setGiftcardData({ id: giftcardId, action: 'remove' });
        removeGiftcard(giftcardId);
        updateQuote();
    };

    return {
        ...state,
        addToCart: onAddToCart,
        removeFromCart: onRemoveFromCart,
        applyGiftcardAndUpdateQuote,
        removeGiftcardAndUpdateQuote
    };
};

export default useCart;
